"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "./utils";
import { buttonVariants } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
	// -----------------------------
	// 👉 State chọn tháng/năm hiện tại
	// -----------------------------

	const [month, setMonth] = React.useState<Date>(props.defaultMonth || new Date());

	const currentYear = new Date().getFullYear();
	const years = React.useMemo(() => {
		const list = [];
		for (let y = 1995; y <= currentYear; y++) list.unshift(y);
		return list;
	}, [currentYear]);

	const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

	// -----------------------------
	// 👉 Render component
	// -----------------------------
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			month={month}
			onMonthChange={setMonth}
			className={cn("p-3", className)}
			classNames={{
				months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
				month: "space-y-4",
				caption: "flex justify-center pt-1 relative items-center",
				caption_label: "text-sm font-medium",
				nav: "space-x-1 flex items-center",
				nav_button: cn(buttonVariants({ variant: "outline" }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"),
				nav_button_previous: "absolute left-1",
				nav_button_next: "absolute right-1",
				table: "w-full border-collapse space-y-1",
				head_row: "flex",
				head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
				row: "flex w-full mt-2",
				cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
				day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
				day_range_end: "day-range-end",
				day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
				day_today: "bg-accent text-accent-foreground",
				day_outside: "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
				day_disabled: "text-muted-foreground opacity-50",
				day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
				day_hidden: "invisible",
				...classNames,
			}}
			components={{
				IconLeft: ({ ...props }) => <ChevronLeft className='h-4 w-4' />,
				IconRight: ({ ...props }) => <ChevronRight className='h-4 w-4' />,
				// ✅ Custom caption (tháng + năm)
				Caption: ({ displayMonth }) => {
					const selectedYear = displayMonth.getFullYear();
					const selectedMonth = displayMonth.getMonth();

					return (
						<div className='flex justify-center items-center space-x-2 py-1'>
							{/* Nút Previous */}
							<button
								type='button'
								className={cn(buttonVariants({ variant: "outline" }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100")}
								onClick={() => setMonth(new Date(selectedYear, selectedMonth - 1, 1))}
							>
								<ChevronLeft className='h-4 w-4' />
							</button>

							{/* Chọn tháng */}
							<Select
								value={selectedMonth.toString()}
								onValueChange={(value: string) => {
									setMonth(new Date(selectedYear, parseInt(value), 1));
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder='Chọn giới tính' />
								</SelectTrigger>
								<SelectContent className='h-40 overflow-y-scroll'>
									{months.map((m, idx) => (
										<SelectItem key={m} value={idx.toString()}>
											{m}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{/* Chọn năm */}
							<Select
								value={selectedYear.toString()}
								onValueChange={(value: string) => {
									setMonth(new Date(parseInt(value), selectedMonth, 1));
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder='Chọn giới tính' />
								</SelectTrigger>
								<SelectContent className='h-40 overflow-y-scroll'>
									{years.map((y) => (
										<SelectItem key={y} value={y.toString()}>
											{y}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{/* Nút Next */}
							<button
								type='button'
								className={cn(buttonVariants({ variant: "outline" }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100")}
								onClick={() => setMonth(new Date(selectedYear, selectedMonth + 1, 1))}
							>
								<ChevronRight className='h-4 w-4' />
							</button>
						</div>
					);
				},
			}}
			{...props}
		/>
	);
}

Calendar.displayName = "Calendar";

export { Calendar };
